// FeasibilityNotice: the honest half of the copilot.
//
// Shown whenever the request didn't fully survive the capability check. It says
// three things and no more: what we couldn't do, why not, and what we did (or
// could do) instead. Amber for "most of it works", rose for "not this one".
// Never a red error toast, because neither case is a failure the owner caused.

import { Ban, CornerDownLeft, TriangleAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { VERDICT_COPY, type CopilotGap, type CopilotVerdict } from "@/lib/workflows/copilot";

interface FeasibilityNoticeProps {
  verdict: CopilotVerdict;
  gaps: CopilotGap[];
  /** A one-click follow-up offered when nothing could be built at all. */
  suggestion?: string;
  onUseSuggestion: (text: string) => void;
}

const TONE = {
  warn: {
    wrap: "border-amber-200 bg-amber-50",
    heading: "text-amber-900",
    body: "text-amber-800",
    rule: "border-amber-200",
    icon: TriangleAlert,
    iconClass: "text-amber-600",
  },
  stop: {
    wrap: "border-rose-200 bg-rose-50",
    heading: "text-rose-900",
    body: "text-rose-800",
    rule: "border-rose-200",
    icon: Ban,
    iconClass: "text-rose-600",
  },
} as const;

export default function FeasibilityNotice({
  verdict,
  gaps,
  suggestion,
  onUseSuggestion,
}: FeasibilityNoticeProps) {
  const copy = VERDICT_COPY[verdict];
  if (copy.tone === "ok" || gaps.length === 0) return null;

  const tone = TONE[copy.tone];
  const Icon = tone.icon;

  return (
    <div
      data-testid="copilot-feasibility-notice"
      className={`rounded-xl border px-4 py-3 ${tone.wrap}`}
    >
      <p className={`flex items-center gap-2 text-xs font-semibold ${tone.heading}`}>
        <Icon className={`h-3.5 w-3.5 shrink-0 ${tone.iconClass}`} />
        {copy.title}
      </p>

      <ul className="mt-2.5 space-y-2.5">
        {gaps.map((gap, index) => (
          <li
            key={`${gap.kind}-${index}`}
            className={index > 0 ? `border-t pt-2.5 ${tone.rule}` : undefined}
          >
            <p className={`text-xs font-medium ${tone.heading}`}>{gap.asked}</p>
            <p className={`mt-0.5 text-xs leading-relaxed ${tone.body}`}>{gap.reason}</p>
            {gap.substitute && (
              <p className={`mt-1.5 text-xs leading-relaxed ${tone.body}`}>
                <span className="font-medium">Instead: </span>
                {gap.substitute}
              </p>
            )}
          </li>
        ))}
      </ul>

      {suggestion && (
        <div className={`mt-3 border-t pt-3 ${tone.rule}`}>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-auto gap-1.5 whitespace-normal bg-background/70 py-1.5 text-left text-xs"
            onClick={() => onUseSuggestion(suggestion)}
          >
            <CornerDownLeft className="h-3.5 w-3.5 shrink-0" />
            {suggestion}
          </Button>
        </div>
      )}
    </div>
  );
}
